import { ReactNode } from 'react';
import { Box } from '@mui/material';
import { Button } from '@qrios/frontend-library';
import { useTheme } from '@mui/material/styles';
import { CheckCircleOutline } from '@mui/icons-material';

import {
  formatDate,
  humanizeDistanceBetweenDates,
  TABLE_DATE_TIME_FORMAT
} from '../../../../../../constants/helpers/dates';
import errors from '../../../../../../constants/errors';
import { CreatedReport, ReportMetadata } from '../../../interfaces';
import { useDownloadReport } from '../../download/useDownloadReport';
import { HandleDownloadReport, HandleOpenDeleteDialog, HandleOpenRegenerateDialog } from '../../interfaces';

import DetailItem from '../detailItem';
import DetailsFactory from './detailsFactory';

interface Props {
  report: CreatedReport;
  generationAllowed: boolean;
  handleOpenDeleteDialog: HandleOpenDeleteDialog;
  handleOpenRegenerateDialog: HandleOpenRegenerateDialog;
  handleDownloadReport: HandleDownloadReport;
  handleCloseDetailsDialog: () => void;
  appliedFilters: (filters: ReportMetadata) => ReactNode;
}

const CreatedDetails = ({
  report,
  generationAllowed,
  handleOpenDeleteDialog,
  handleCloseDetailsDialog,
  handleOpenRegenerateDialog,
  handleDownloadReport: downloadFile,
  appliedFilters
}: Props) => {
  const { handleDownloadReport } = useDownloadReport({
    id: report.id,
    name: report.name,
    downloadFile
  });

  const theme = useTheme();

  return (
    <DetailsFactory
      action={{
        icon: { component: CheckCircleOutline, color: theme.palette.success.main },
        text: 'Report generated and ready to download.',
        content: (
          <>
            <Box width="100%" mb={2} mt={2}>
              <Button
                fullWidth
                onClick={handleDownloadReport}
                text="Download report"
                data-testid="button-download-report"
              />
            </Box>

            <Box width="100%">
              <Button
                fullWidth
                onClick={() => {
                  handleOpenDeleteDialog(report);
                  handleCloseDetailsDialog();
                }}
                text="Delete report"
                danger
                data-testid="button-delete-report"
              />
            </Box>
          </>
        )
      }}
      metaContent={
        <>
          <DetailItem label="Created date" value={formatDate(report.createDate, TABLE_DATE_TIME_FORMAT)} />
          <DetailItem label="Time until expiry" value={humanizeDistanceBetweenDates(report.expireDate)} />
        </>
      }
      filtersContent={
        <>
          <DetailItem label="Applied Filters" value={appliedFilters(report.metadata)} />
        </>
      }
    />
  );
};

export default CreatedDetails;
